import DropdownStore from './DropdownStore';
import { getDefaultActiveKey, setActiveDataToObject } from './util';
import { IDropdownLayerConfig, LayerType, NewDataType, IItem, ActiveDataType, StateType, ActiveDataObjType } from "../interface/IDropdown";

export default class DropdownLayer {
    private layerEle: HTMLDivElement | null = null;
    private data: NewDataType;
    private layerType: LayerType;
    private useFitModeExceptPc: boolean;
    private defaultActiveKey: string;
    private changeCallback: ((curVal: ActiveDataObjType) => any) | null;
    private store: DropdownStore;

    constructor(_config: IDropdownLayerConfig) {
        const {data, parent, layerType, useFitModeExceptPc, changeCallback, store} = _config;

        this.store = store;
        this.data = data;
        this.layerType = layerType;
        this.useFitModeExceptPc = useFitModeExceptPc;
        this.defaultActiveKey = getDefaultActiveKey(this.store.state.maxDepth);
        this.changeCallback = changeCallback;

        this.init(parent);
    }

    private init(parent: Element) {
        this.renderLayerWrap(parent);

        if(this.layerType === 'basic') {
            this.renderLayerBasic();
        } else {
            this.renderLayerWide();
        }

        this.store.addListener('isOpen', (isOpen: boolean) => {
            isOpen ? this.open() : this.close();
        });

        this.store.addListener('activeKey', (activeKey: string) => {
            const { activeData } = this.store.state;
            if(activeData?.length === 0) return;

            this.setValue(activeKey, activeData);
        });
    }

    private templateMenuWrap(): string {
        let tmpl = '';

        for(let depth = 1; depth <= this.store.state.maxDepth; depth++) {
            tmpl += `<div class="menu-wrap menu-${depth}"></div>`;
        }
        
        return tmpl;
    }

    /*
        뎁스별 리스트 템플릿
        @param {IItem[]} data 뎁스별 리스트 데이터
        @return {string} tmpl 템플릿
    */
    private templateMenuList(data?: IItem[]): string {
        let tmpl = '';

        if(Array.isArray(data) && data?.length > 0) {
            let listTmpl = '';

            data.forEach((v: IItem) => {
                listTmpl += `<li>
                    <a href="#" data-value="${v?.value || ''}" data-key=${v?.key || ''}>${v?.title || ''}</a>
                </li>`;
            });

            tmpl = `<ul>${listTmpl}</ul>`;
        }
        
        return tmpl;
    }

    /*
        dropdownlayer wrap 동적 생성
        @param {Element} parent dropdownlayer wrap append할 부모 element
    */
    private renderLayerWrap(parent: Element) {
        const wrapTmpl = `<div class="dropdown-layer-wrap layer-${this.layerType}" ${this.useFitModeExceptPc ? `style="--menu-cols:${this.store.state.maxDepth};"` : ''}></div>`;
        parent?.insertAdjacentHTML('beforeend', wrapTmpl.trim());

        this.layerEle = parent.querySelector('.dropdown-layer-wrap');
    }

    /*
        dropdownlayer 기본 타입 (2단, 3단, ... 단 구성)
    */
    private renderLayerBasic() {
        const tmpl = this.templateMenuWrap();
        this.layerEle?.insertAdjacentHTML('beforeend', tmpl);
        this.renderMenuList(0, this.defaultActiveKey);
    }

    /*
        dropdownlayer wide 타입 (시세 등 1뎁스 첫번째 요소가 width: 100%로 노출)
    */
    private renderLayerWide() {
        if(!Array.isArray(this.data[0]) || this.data[0].length === 0) {
            return;
        }

        const depth1Data = this.data[0];
        const firstData = depth1Data[0];
        const tmpl = `<div class="menu-wide">
            <a href="#" data-value="${firstData?.value || ''}" data-key="${firstData?.key || ''}">
                ${firstData?.title || ''}
            </a>
        </div>
        <div class="menu-list">
            ${this.templateMenuWrap()}
        </div>`;

        this.layerEle?.insertAdjacentHTML('beforeend', tmpl);
        this.renderMenuList(0, this.defaultActiveKey);
    }


    /*
        뎁스별 리스트 동적 생성
        @param {number} depth 리스트를 생성할 뎁스
        @param {string} activeKey 현재 활성화된 마지막 요소의 키값
    */
    private renderMenuList(depth: number = 0, activeKey: string) {
        const { maxDepth } = this.store.state;
        const activeSplitKey = activeKey ? activeKey?.split('-') : getDefaultActiveKey(maxDepth)?.split('-');

        for(let i = depth; i < maxDepth; i++) {
            const $wrap = this.layerEle?.querySelector(`.menu-${i + 1}`);
            if($wrap) {
                const key = activeSplitKey.slice(0, i).join('-');
                const data = this.data[i];
                const depthData = Array.isArray(data) ? data : data.get(key) || [];
                const copyDepthData = Array.isArray(depthData) ? [...depthData] : [];

                if(this.layerType === 'wide' && i === 0) {
                    copyDepthData.splice(0, 1);
                }
                
                $wrap.innerHTML = this.templateMenuList(copyDepthData);
            }
        }
    }

    /*
        레이어 오픈, 닫기 시 document 이벤트 add/remove
        @param {boolean} isAddEvent event 추가 여부
    */
    private toggleDocumentClickEvent(isAddEvent: boolean) {
        if(isAddEvent) {
            document.addEventListener('click', this.addDocumentClickEvent);
        } else {
            document.removeEventListener('click', this.addDocumentClickEvent);
        }
    }

    /*
        document 이벤트 핸들러
        @param {MouseEvent} e event 객체
    */
    private addDocumentClickEvent = (e: MouseEvent) => {
        const target = e.target as HTMLAnchorElement;
        const isTargetContainsDropdown = !!target.closest('.ncui-dropdown-wrap');
        const isTargetContainsLayer = this.layerEle?.contains(target);

        // 버튼 이벤트 위임 (this.$layer안의 a태그 / button 태그)
        if(isTargetContainsLayer && (target.tagName === 'A' || target.tagName === 'button')) {
            e.preventDefault();
            e.stopPropagation();

            if(target?.classList?.contains('is-active')) {
                return;
            }

            const key = target.dataset.key || null;
            
            this.store.dispatch<'prevActiveKey', StateType['prevActiveKey']>('prevActiveKey', this.store.state.activeKey);
            this.store.dispatch<'activeKey', StateType['activeKey']>('activeKey', key);
            
            // 마지막 뎁스값 선택 시
            const { isLastDepth, activeData } = this.store.state;
            if(isLastDepth) {
                typeof this.changeCallback === 'function' && this.changeCallback(setActiveDataToObject(activeData));
                this.store.dispatch<'isOpen', StateType['isOpen']>('isOpen', false);
            }

            return;
        }

        // 외부 영역 클릭 시 dropdown 닫힘
        if(!isTargetContainsDropdown && !isTargetContainsLayer) {
            this.store.dispatch<'isOpen', StateType['isOpen']>('isOpen', false);
        }
    }

    /*
        메뉴 a태그 스타일 활성화
        @param {ActiveDataType} activeData 활성화하려는 뎁스별 데이터
    */
    private dropdownActive(activeData: ActiveDataType) {
        activeData?.forEach((v, i) => {
            const depth = i + 1;
            let $wrap = this.layerEle?.querySelector(`.menu-${depth}`);
            let $prevDropdown = $wrap?.querySelector(`.is-active`);
            let $curDropdown = $wrap?.querySelector(`[data-key="${v?.key || ''}"]`);

            if(this.layerType === 'wide' && i === 0) {
                const $menuWide = this.layerEle?.querySelector(`.menu-wide`);
                const $menuWideDropdown = $menuWide?.querySelector('a');

                if(v?.key === '0') {
                    $curDropdown = $menuWideDropdown;
                } else {
                    if($menuWideDropdown?.classList?.contains('is-active')) {
                        $prevDropdown = $menuWideDropdown;
                    }
                }                
            }

            $prevDropdown?.classList?.remove('is-active');
            $curDropdown?.classList?.add('is-active');
        });
    }

    /*
        이전, 현재 activeKey에서 depth별 index가 다른 depth 찾기
        @param {string} activeKey activeKey 활성화하려는 마지막 뎁스 요소의 키값
        @return {number | null} diffDepth 기존과 index값이 변경된 뎁스
    */
    private findDiffDepth(activeKey: string): number | null {
        const curSplitKey = activeKey?.split('-') || [];
        const prevSplitKey = this.store.state.prevActiveKey?.split('-') || [];
        let diffDepth = null;

        // 이전, 현재 activeKey에서 depth별 index가 다른 depth 찾기
        const hasDiffDepth = curSplitKey.some((depthIndex, i) => {
            if(depthIndex !== prevSplitKey[i]) {
                diffDepth = i;
                return true;
            }
        });

        if(!hasDiffDepth && (curSplitKey?.length !== prevSplitKey?.length)) {
            diffDepth = curSplitKey?.length < prevSplitKey?.length ? prevSplitKey?.length - 1 : curSplitKey?.length - 1;
        }

        return diffDepth;
    }

    open() {
        this.layerEle?.classList?.add('is-open');
        this.toggleDocumentClickEvent(true);
    }

    close() {
        this.layerEle?.classList?.remove('is-open');
        this.toggleDocumentClickEvent(false);
    }
    
    /*
        activeKey / activeData에 따른 DropdownLayer 활성화
        @param {string} activeKey 활성화하려는 마지막 뎁스 요소의 키값
        @param {ActiveDataType} activeData 활성화하려는 뎁스별 데이터
    */
    setValue(activeKey: string, activeData: ActiveDataType) {
        const diffDepth = this.findDiffDepth(activeKey);

        // diffDepth부터 새로 렌더링
        if(typeof diffDepth === 'number') {
            this.renderMenuList(diffDepth, activeKey);
        }

        this.dropdownActive(activeData);
    }
}